import Tooltip from '@mui/material/Tooltip';
import { Link, useLocation } from "react-router-dom";
import { VscNewFile } from "react-icons/vsc";
import { FaUser } from "react-icons/fa";
import { MdLocationPin } from "react-icons/md";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Box, Grid, Card, CardContent, Typography, useMediaQuery, useTheme, CircularProgress, Button, IconButton, TextField, Snackbar, Chip, Alert, Checkbox, Paper, InputAdornment, Stack, Avatar, Divider, Popover, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, RadioGroup, Radio, FormControlLabel, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import PdfViewer from '../components/PdfViewer';
import { useAuth } from '../context/AuthContext';
import useDataLoadingState from '../hooks/useDataLoadingState';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatRows } from '../utils/formatRows';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import CenterLoader from '../components/CenterLoader';
import { MessageRounded } from '@mui/icons-material';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { APPBAR_HEIGHT, colors as themeColors, gradients } from '../utils/theme';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import LoadingOverlay from '../components/LoadingOverlay';
import SkeletonLoader from '../components/SkeletonLoader';
import { motion } from 'framer-motion';

const getFinancialYearRange = (now = new Date()) => {
    const month = now.getMonth();
    const startYear = month >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` };
};

const normalizeDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-").reverse().join("/") : v;

// Helper to generate avatar color
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4'];
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return 'C';
    return name.trim().charAt(0).toUpperCase();
}

export default function QuotationDetail({ connectionString, pdfString }) {
    const PDF_API_BASE = pdfString;
    const { user, selectedCompany } = useAuth();
    const api = connectionString;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory;
    const params = { userId, userCategory: role, companyId };
    console.log(companyId);

    const newWinRef = useRef(null);

    const [genQuoteData, setGenQuoteData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectionModel, setSelectionModel] = useState([]);

    // REMOVE these old snackbar states - they conflict with the unified snackbar state below
    // const [snackbarOpen, setSnackbarOpen] = useState(false);
    // const [snackbarMessage, setSnackbarMessage] = useState("");
    // const [snackbarJoinedMessage, setSnackbarJoinedMessage] = useState([])
    // const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [pdfOverlayVisible, setPdfOverlayVisible] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // SINGLE UNIFIED SNACKBAR STATE - Use this consistently everywhere
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Filter popup states
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPopupMode, setFilterPopupMode] = useState('datewise');
    const [filterPopupFromDate, setFilterPopupFromDate] = useState('');
    const [filterPopupToDate, setFilterPopupToDate] = useState('');
    const [filterPopupFirms, setFilterPopupFirms] = useState([]);
    const [firmOptions, setFirmOptions] = useState([]);
    const [firmAllOptions, setFirmAllOptions] = useState([]);
    const [firmInputValue, setFirmInputValue] = useState('');
    const [firmLoading, setFirmLoading] = useState(false);
    const [filterPopupSnapshot, setFilterPopupSnapshot] = useState(null);
    const [exportExcelEnabled, setExportExcelEnabled] = useState(false);
    const [masterReportData, setMasterReportData] = useState([]);
    const [detailsReportData, setDetailsReportData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const [pdfUri, setPdfUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [pdfApiUrl, setPdfApiUrl] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const openConfirm = Boolean(anchorEl);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // 🔥 Add loading state hook for unified loading display
    const { showLoadingSpinner, showEmptyState } = useDataLoadingState(loading, genQuoteData?.length > 0);
    const { showLoadingSpinner: showReportSpinner, showEmptyState: showReportEmpty } = useDataLoadingState(reportLoading, masterReportData?.length > 0);

    useEffect(() => {
        fetchGenQuote();
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUri) {
                try { URL.revokeObjectURL(pdfUri); } catch (e) { }
            }
            try {
                if (newWinRef.current && !newWinRef.current.closed) {
                    newWinRef.current.close();
                }
            } catch (e) { }
        };
    }, []);

    const fetchGenQuote = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${api}/api/quotationmaster`, { params, headers: { "Cache-Control": "no-store" } });
            console.log('Fetched quotation data:', data);
            setGenQuoteData(formatRows(data.map((r) => ({ ...r, id: r.QoutId, quotationNumber: r.QoutNumber, quotationDate: r.QoutDate, referenceNumber: r.RefNo, subTotal: r.BillAmt }))));
        } catch (err) {
            console.error("fetchGenQuote error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (selectionModel.length === 0) {
            setSnackbar({ open: true, message: "No quotations selected for deletion.", severity: 'warning' });
            setTimeout(() => {
                setSnackbar(prev => ({ ...prev, open: false }));
            }, 3000);
            return;
        }

        setOpenDeleteDialog(true);
        console.log("Deleting IDs:", selectionModel);
    };

    const confirmDelete = async () => {
        if (deleting) return;
        setDeleting(true);

        try {
            const payload = { objAryId: selectionModel, companyId };
            const { data } = await axios.post(`${api}/api/delete-quotations`, payload);
            console.log("Delete API response:", data);

            const { deletedQuotations = 0, referencedIds = [] } = data || {};

            let message = "";
            let severity = "success";

            if (referencedIds.length > 0 && deletedQuotations > 0) {
                message = "Deleted Successfully.";
                severity = "success";
            }
            else if (referencedIds.length > 0 && deletedQuotations === 0) {
                message = "Cannot delete the selected item(s) because they are referenced in other records.";
                severity = "warning";
            }
            else if (deletedQuotations > 0) {
                message = "Deleted Successfully.";
                severity = "success";
            }

            setSnackbar({ open: true, message, severity });
            setSelectionModel([]);
            fetchGenQuote();

        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;

            if (status === 400 && data?.referencedIds?.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: 'warning' });
            } else if (status === 400 && data?.error) {
                setSnackbar({ open: true, message: data.error, severity: 'warning' });
            } else {
                setSnackbar({ open: true, message: "Delete failed due to an unexpected error.", severity: 'error' });
            }
        } finally {
            setDeleting(false);
            setOpenDeleteDialog(false);
        }
    };

    const generatedColumns = useMemo(() => {
        const cols = [
            { field: "quotationDate", headerName: "Quotation Date", flex: 0.5, minWidth: 90, filterable: true, valueGetter: (value) => normalizeDate(value), valueFormatter: (params) => normalizeDate(params) },
            {
                field: "quotationNumber", headerName: "Quotation No.", flex: 0.6, minWidth: 110, filterable: true,
                renderCell: (params) => (<Typography component={Link} to={`/main/v3/editquatationMaster/${params.row.QoutId}`} sx={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} > {params.value} </Typography>)
            },
            {
                field: "clientName", headerName: "FirmName", flex: 2, minWidth: 180,
                renderCell: (params) => (
                    <Box sx={{ cursor: "pointer", whiteSpace: "normal", wordBreak: "break-word", my: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}> {params.row.FirmName} </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FaUser color={themeColors.warning.main} />
                            <Typography variant="body2"> {params.row.ContactPerson || "-"} </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary"> {" "} 📲 {params.row.MobileNo || "-"}{" "} </Typography>
                    </Box>
                )
            },
            {
                field: "location", headerName: "Location", flex: 0.7, minWidth: 100, sortable: true, filterable: true,
                valueGetter: (value, row) => [row.City, row.state].filter(Boolean).join(", "),
                renderCell: (params) => (
                    <Box sx={{ cursor: "pointer", whiteSpace: "normal", wordBreak: "break-word", my: 1 }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span>{params.row.City}</span>
                            <span style={{ fontSize: "0.8em", color: themeColors.neutral.sub }}>{params.row.state}</span>
                        </div>
                    </Box>
                )
            },
            {
                field: "subTotal", headerName: "Total Amt", flex: 0.5, minWidth: 110, align: "right", headerAlign: "right",
                valueFormatter: (params) => { return `₹${Number(params || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
            },
            {
                field: "actions", headerName: "Action", flex: 0.4, minWidth: 45, sortable: false, filterable: false, align: "right", headerAlign: "right",
                renderCell: (params) => (
                    <IconButton color="primary" onClick={() => handleViewClick(params.row.QoutId)}>
                        <PrintIcon />
                    </IconButton>
                )
            }
        ];

        if (role !== "User") {
            cols.push({
                field: "delete", headerName: "Delete", flex: 0.3, minWidth: 45, sortable: false, filterable: false, align: "right", headerAlign: "right",
                renderCell: (params) => {
                    const isSelected = selectionModel.includes(params.row.QoutId);
                    return (
                        <Tooltip title="Delete selected quotation(s)">
                            <span>
                                <IconButton color="error" disabled={!isSelected} size="small" aria-label="delete" onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete();
                                }} > <DeleteIcon /> </IconButton>
                            </span>
                        </Tooltip>
                    );
                }
            });
        }

        return cols;
    }, [selectionModel, role]);

    const handleViewClick = async (QoutId) => {
        if (isGeneratingPdf) return;
        setPdfOverlayVisible(true);
        setIsGeneratingPdf(true);

        if (!QoutId) {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            return;
        }

        const userIdSafe = userId || "usr1";

        let formatId = "format_1";
        let bookNameToSend = "Quotation";
        let bookIdToSend = null;

        try {
            const found = genQuoteData.find((g) => Number(g.QoutId) === Number(QoutId));
            if (found) {
                if (found.BookName) bookNameToSend = String(found.BookName).trim();
                else if (found.bookName) bookNameToSend = String(found.bookName).trim();
            }
        } catch (err) {
            bookNameToSend = "Quotation";
        }

        let matchedFormat = null;
        try {
            // ✅ Add cache-buster to get fresh data
            const cacheBreaker = new Date().getTime();
            const resp = await axios.get(`${api}/api/companies`, { params: { companyId, t: cacheBreaker } });
            let comps = [];
            if (Array.isArray(resp?.data?.companies)) comps = resp.data.companies;
            else if (Array.isArray(resp?.data)) comps = resp.data;
            else if (resp?.data) comps = [resp.data];

            let matched = null;
            const eq = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
            const targetForm = "Quotation";

            for (const c of comps) {
                if (Array.isArray(c.formats)) {
                    for (const f of c.formats) {
                        const bn = String(f.BookName || f.bookName || "").trim();
                        const fn = String(f.FormName || f.formName || "").trim();
                        if (eq(bn, bookNameToSend) || eq(fn, targetForm)) {
                            matched = { company: c, format: f };
                            break;
                        }
                    }
                    if (matched) break;
                }
                const bnFlat = String(c.BookName || c.bookName || "").trim();
                const fnFlat = String(c.FormName || c.formName || "").trim();
                if (eq(bnFlat, bookNameToSend) || eq(fnFlat, targetForm)) {
                    matched = { company: c, format: c };
                    break;
                }
            }

            if (matched) {
                const f = matched.format;
                const foundBookId = f.BookID ?? f.BookId ?? f.bookId ?? null;
                if (foundBookId) {
                    formatId = `format_${String(foundBookId)}`;
                    bookIdToSend = foundBookId;
                } else {
                    const foundFormName = f.FormName ?? f.formName ?? null;
                    if (foundFormName) formatId = String(foundFormName);
                }
                bookNameToSend = f.BookName ?? f.bookName ?? bookNameToSend;
                // ✅ Store matched format for Signature checking (fresh from API)
                matchedFormat = f;
            }
        } catch (err) {
            formatId = "format_1";
            bookNameToSend = bookNameToSend || "Quotation";
            bookIdToSend = null;
        }

        let apiUrl = `${PDF_API_BASE}/api/pdf/Quotation/${encodeURIComponent(QoutId)}?userId=${encodeURIComponent(userIdSafe)}&format=${encodeURIComponent(formatId)}&bookName=${encodeURIComponent(bookNameToSend)}`;
        if (bookIdToSend != null) {
            apiUrl += `&bookId=${encodeURIComponent(String(bookIdToSend))}`;
        }
        setPdfApiUrl(apiUrl);

        let imageBase64 = null;
        let signatureBase64 = null;
        try {
            const iconRes = await axios.get(`${api}/api/company/GetIcon/${encodeURIComponent(companyId)}`);
            if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
                imageBase64 = iconRes.data.imageBase64;
            }
            if (iconRes?.data?.signatureBase64) {
                signatureBase64 = iconRes.data.signatureBase64;
            }
        } catch (err) {
            console.warn("Failed to fetch company icon:", err);
        }
        // ✅ Use matched format's Signature (fresh from API, not cached context)
        if (!matchedFormat?.Signature) signatureBase64 = null;

        try {
            setPdfLoading(true);

            const quoteResp = await axios.get(`${api}/api/get-quote-data/${QoutId}`, { params: { companyId }, headers: { "Cache-Control": "no-store" } });

            const quoteData = quoteResp.data || {};
            const master = quoteData.master || null;
            const details = quoteData.details || [];
            const companyDetails = quoteData.company || null;
            const termsAndConditions = quoteData.termsAndConditions || null;
            const additionalChargeItems = quoteData.additionalChargeItems || [];

            if (!master) throw new Error("No master data");
            const termsText = Array.isArray(termsAndConditions) && termsAndConditions.length > 0
                ? termsAndConditions
                    .sort((a, b) => a.SrNo - b.SrNo)
                    .map(t => `# ${t.TermsDescription}`)
                    .join('\n')
                : null;

            const companyIdFromMaster = master.CompID || master.CompId || companyId || null;

            const items = (details || []).map((d) => {
                const qtyNum = Number(d.Qty) || 0;
                const rateNum = Number(d.Rate) || 0;
                const discNum = Number(d.Disc) || 0;
                const lineTotal = qtyNum * rateNum * (1 - discNum / 100);
                return {
                    ItemCode: d.itemCode || "",
                    productImage: d.productImage || "",
                    hsnCode: d.HSNNo || "",
                    Remark: d.Remark || "",
                    description: `${d.productDescription || ""}`,
                    brand: d.brand || "",
                    ItemGrpID: d.ItemGrpID || null,
                    qty: d.Qty || "",
                    rate: d.Rate || "",
                    Unit: d.Unit || "",
                    discount: d.Disc || "",
                    cgst: parseFloat(d.CGST) || 0,
                    sgst: parseFloat(d.SGST) || 0,
                    igst: parseFloat(d.IGST) || 0,
                    amount: lineTotal,
                    TaxableAmt: Number(d.TaxableAmt) || 0,
                    Group: d.Group || "Ungrouped"
                };
            });

            // Additional Charges — bound directly from Adc1Amt..Adc5Amt on
            // master; the name for each slot comes from productmaster.description
            // where TransType = 199 + slot (200-204), via additionalChargeItems.
            const additionalCharges = [1, 2, 3, 4, 5]
                .map(slot => {
                    const chargeItem = (additionalChargeItems || []).find(ci => Number(ci.TransType) === 199 + slot);
                    console.log('chargeItem', slot, chargeItem);
                    return { name: chargeItem?.description || `Charge ${slot}`, amount: Number(master[`Adc${slot}Amt`]) || 0, };
                })
                .filter(c => c.amount > 0);
            console.log('additionalCharges', JSON.stringify(additionalCharges, null, 2));

            const pdfData = {
                companyId: companyIdFromMaster,
                companyName: master.FirmName || "",
                address: master.Address || "",
                phone: master.MobileNo || "",
                gstNo: master.GSTNo || "",
                invoiceNo: master.QoutNumber,
                referrenceNo: master.RefNo,
                date: master.QoutDate ? normalizeDate(master.QoutDate) : "",
                transport: master.Transporter || "SELF",
                challanNo: master.lrNumber || "",
                poNo: master.Project || "",
                state: master.state || "",
                stateCode: master.StateCode || "",
                LrNo: master.lrNumber || "",
                vehNo: master.vehicleNumber || "",
                LrDate: master.lrDate ? normalizeDate(master.lrDate) : "",
                shipToGstNumber: master.shipToGstNumber || "",
                shipToState: master.state || "",
                items,
                toEmail: master.EmailId || "",
                totalAmount: master.grandTotal || 0,
                mobileNo: master.MobileNo || "",
                basicAmount: Number(master.TaxableAmt) || 0,
                additionalCharges,
                CGSTAmt: Number(master.CGSTAmt) || 0,
                SGSTAmt: Number(master.SGSTAmt) || 0,
                IGSTAmt: Number(master.IGSTAmt) || 0,
                RoundOff: Number(master.RoundOff) || 0,
                BillAmt: Number(master.BillAmt) || 0,
                clientState: master.clientState || "",
                clientStateCode: master.clientStateCode || "",
                imageBase64: imageBase64 || null,
                Remark: master.Remark || null,
                termsAndConditions: termsText,
                PaymentTerm: master.PaymentTerm || null,
            };

            const companyHeader = companyDetails ? {
                CompId: companyDetails.CompId,
                CompanyName: companyDetails.CompanyName || "",
                Address: companyDetails.Address || "",
                MobileNo: companyDetails.MobileNo || "",
                EmailId: companyDetails.EmailId || "",
                Website: companyDetails.Website || "",
                GSTNo: companyDetails.GSTNo || "",
                imageBase64: imageBase64 || null,
                city: companyDetails.City || null,
                BkName1: companyDetails.BkName1 || null,
                BkBranch1: companyDetails.BkBranch1 || null,
                BkAcNo1: companyDetails.BkAcNo1 || null,
                BkIFSCCode1: companyDetails.BkIFSCCode1 || null,
                // ✅ NEW: Include Color from matchedFormat for PDF rendering
                headerColor: matchedFormat?.Color || themeColors.neutral.black,
            } : null;

            const pdfResp = await axios.post(
                `${PDF_API_BASE}/api/pdf/Quotation/${encodeURIComponent(QoutId)}`,
                {
                    docId: QoutId,
                    userId: userIdSafe,
                    formatId,
                    data: pdfData,
                    company: { ...companyHeader, imageBase64: imageBase64 || null, signatureBase64: signatureBase64 || null }
                },
                { responseType: "blob" }
            );

            const blob = pdfResp.data;
            if (!blob || blob.size === 0) throw new Error("Downloaded PDF is empty");

            const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            if (window.ReactNativeWebView) {
                const reader = new FileReader();
                reader.onloadend = () => { window.ReactNativeWebView.postMessage( JSON.stringify({ type: "OPEN_PDF", base64: reader.result }) ); };
                reader.readAsDataURL(blob);
            } else {
                const newWin = window.open("", "_blank");
                if (newWin) {
                    newWinRef.current = newWin;
                    newWin.location.href = blobUrl;
                    newWin.focus();

                    const checker = setInterval(() => {
                        if (newWin.closed) {
                            URL.revokeObjectURL(blobUrl);
                            clearInterval(checker);
                            newWinRef.current = null;
                        }
                    }, 1000);
                } else {
                    if (pdfUri) URL.revokeObjectURL(pdfUri);
                    setPdfUri(blobUrl);
                    setSnackbar({ open: true, message: "Popup blocked — showing PDF inline.", severity: 'warning' });
                }
            }
        } catch (err) {
            setPdfError(err?.message || "Failed to generate PDF");
            if (newWinRef.current && !newWinRef.current.closed) {
                newWinRef.current.close();
                newWinRef.current = null;
            }
            setSnackbar({ open: true, message: "Failed to generate PDF. Please try again.", severity: 'error' });
        } finally {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
        }
    };

    const filteredGenQuoteData = useMemo(() => {
        if (!filterText) return genQuoteData;
        const lower = filterText.toLowerCase();
        return genQuoteData.filter(row =>
            String(row.QoutNumber || "").toLowerCase().includes(lower) ||
            String(row.FirmName || "").toLowerCase().includes(lower)
        );
    }, [genQuoteData, filterText]);

    const handleSelectAllVisible = () => {
        const visibleIds = filteredGenQuoteData.map(row => row.QoutId);
        const allSelected = visibleIds.every(id => selectionModel.includes(id));
        if (allSelected) {
            setSelectionModel(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectionModel(prev => [...new Set([...prev, ...visibleIds])]);
        }
    };

    const allVisibleSelected = useMemo(() => {
        const visibleIds = filteredGenQuoteData.map(row => row.QoutId);
        return visibleIds.length > 0 && visibleIds.every(id => selectionModel.includes(id));
    }, [filteredGenQuoteData, selectionModel]);

    const renderGeneratedCards = () => {
        if (loading) return <SkeletonLoader count={3} height={120} />;
        if (filteredGenQuoteData.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredGenQuoteData.map((row) => {
            const isSelected = selectionModel.includes(row.QoutId);
            const avatarColor = stringToColor(row.FirmName || 'Quotation');
            const initials = getInitials(row.FirmName);
            const locationText = [row.City, row.state].filter(Boolean).join(', ') || '—';

            const toggleSelection = (e) => {
                e.stopPropagation();
                setSelectionModel(prev => prev.includes(row.QoutId) ? prev.filter(id => id !== row.QoutId) : [...prev, row.QoutId]);
            };

            return (
                <Card key={row.QoutId} onClick={() => navigate(`/main/v3/editquatationMaster/${row.QoutId}`)} sx={{ mb: 2, borderLeft: `6px solid ${themeColors.primary.main}`, borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'background.paper', cursor: 'pointer', '&:hover': { boxShadow: 6 }, mx: { xs: 1, sm: 0 }, }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                {role !== 'User' && (
                                    <Checkbox checked={isSelected} onChange={toggleSelection} onClick={(e) => e.stopPropagation()} size="small" />
                                )}
                                <Avatar sx={{ bgcolor: avatarColor, color: themeColors.neutral.surface, width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>
                                        {row.FirmName || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {locationText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap>
                                {normalizeDate(row.QoutDate)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.QoutNumber || '—'}
                                </Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                ₹{row.BillAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>

                        {row.RefNo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <LinkIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.RefNo}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                <PersonIcon sx={{ fontSize: 18, color: themeColors.warning.main, mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {row.ContactPerson || 'N/A'}
                                </Typography>
                            </Box>
                            <IconButton
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewClick(row.QoutId);
                                }}
                                sx={{ flexShrink: 0 }}
                            >
                                <PrintIcon />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    // UNIFIED SNAKBAR CLOSE HANDLER - Works with unified snackbar state
    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const openFilterPopup = useCallback(() => {
        const { from, to } = getFinancialYearRange(new Date());
        const fromDate = filterPopupFromDate || from;
        const toDate = filterPopupToDate || to;
        if (!filterPopupFromDate) setFilterPopupFromDate(fromDate);
        if (!filterPopupToDate) setFilterPopupToDate(toDate);
        setFilterPopupSnapshot({ mode: filterPopupMode, fromDate, toDate, firms: filterPopupFirms });
        setFilterPopupOpen(true);
    }, [filterPopupFromDate, filterPopupToDate, filterPopupMode, filterPopupFirms]);

    const cancelFilterPopup = useCallback(() => {
        if (filterPopupSnapshot) {
            setFilterPopupMode(filterPopupSnapshot.mode);
            setFilterPopupFromDate(filterPopupSnapshot.fromDate);
            setFilterPopupToDate(filterPopupSnapshot.toDate);
            setFilterPopupFirms(filterPopupSnapshot.firms || []);
        }
        setFilterPopupOpen(false);
    }, [filterPopupSnapshot]);

    const handleFilterOk = useCallback(async () => {
        setExportExcelEnabled(true);

        if (!companyId) {
            setSnackbar({
                open: true,
                message: 'Company not selected',
                severity: 'warning'
            });
            return;
        }

        if (!filterPopupFromDate || !filterPopupToDate) {
            setSnackbar({
                open: true,
                message: 'Please select date range',
                severity: 'warning'
            });
            return;
        }

        setReportLoading(true);

        try {
            const filterParams = {
                companyId,
                fromDate: filterPopupFromDate,
                toDate: filterPopupToDate,
                mode: filterPopupMode,
                firms: filterPopupFirms.length > 0
                    ? filterPopupFirms.map(f => f.id).join(',')
                    : '',
            };

            const response = await axios.get(
                `${api}/api/quotation-register`,
                { params: filterParams }
            );

            const masterData = response?.data?.master?.data || [];
            const detailsData = response?.data?.details?.data || [];

            setMasterReportData(masterData);
            setDetailsReportData(detailsData);

            setSnackbar({
                open: true,
                message: `Report loaded: ${masterData.length} master records, ${detailsData.length} detail records`,
                severity: 'success'
            });

            console.log('Master Report Data:', masterData);
            console.log('Details Report Data:', detailsData);

        } catch (err) {
            console.error('Error fetching report data:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'Failed to fetch report data',
                severity: 'error'
            });
        } finally {
            setReportLoading(false);
        }
    }, [
        companyId,
        filterPopupFromDate,
        filterPopupToDate,
        filterPopupMode,
        filterPopupFirms,
        api
    ]);

    const handleExportExcel = useCallback(() => {
        if (!masterReportData.length && !detailsReportData.length) {
            setSnackbar({ open: true, message: 'No data to export. Please apply filters first.', severity: 'warning' });
            return;
        }

        const formatDate = (val) => {
            if (!val) return '';
            const s = String(val).split('T')[0];
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                const [y, m, d] = s.split('-');
                return `${d}-${m}-${y}`;
            }
            return val;
        };

        const buildSheet = (data, columns) => {
            const header = columns.map(c => c.header);
            const rows = data.map(row =>
                columns.map(c => {
                    const val = row[c.key] ?? '';
                    if (c.isDate) return formatDate(val);
                    if (val === 0 || val === '0' || val === 0.0) return '';
                    // Numeric columns often arrive from the API as strings (e.g. SQL
                    // DECIMAL fields serialized as text). aoa_to_sheet writes cells
                    // based on the JS value's type, so a string here becomes a text
                    // cell that Excel's status bar (Sum/Average) will not aggregate.
                    // Coerce to a real number so these columns total correctly.
                    if (c.isNumeric) {
                        const num = Number(val);
                        return Number.isNaN(num) ? val : num;
                    }
                    return val;
                })
            );
            return XLSX.utils.aoa_to_sheet([header, ...rows]);
        };

        const masterColumns = [
            { key: 'QoutDate', header: 'BillDate', isDate: true },
            { key: 'QoutNumber', header: 'BillNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'GSTCEssAmt', header: 'GSTCessAmt', isNumeric: true },
            { key: 'BillAmt', header: 'BillAmt', isNumeric: true },
            { key: 'Remark', header: 'Remark' },
        ];

        const detailsColumns = [
            { key: 'QoutDate', header: 'BillDate', isDate: true },
            { key: 'QoutNumber', header: 'BillNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'itemCode', header: 'ItemCode' },
            { key: 'description', header: 'Description' },
            { key: 'HSNNo', header: 'HSNNo' },
            { key: 'Unit', header: 'Unit' },
            { key: 'QTY', header: 'Qty', isNumeric: true },
            { key: 'Rate', header: 'Rate', isNumeric: true },
            { key: 'Disc', header: 'Disc', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'CGSTRate', header: 'CGSTRate', isNumeric: true },
            { key: 'SGSTRate', header: 'SGSTRate', isNumeric: true },
            { key: 'IGSTRate', header: 'IGSTRate', isNumeric: true },
            { key: 'CESSRate', header: 'CESSRate', isNumeric: true },
            { key: 'UTGSTRate', header: 'UTGSTRate', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'CESSAmt', header: 'CESSAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'ItemRemark', header: 'ItemRemark' },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, buildSheet(masterReportData, masterColumns), 'Master');
        XLSX.utils.book_append_sheet(wb, buildSheet(detailsReportData, detailsColumns), 'Details');

        const fromDate = filterPopupFromDate || 'start';
        const toDate = filterPopupToDate || 'end';
        XLSX.writeFile(wb, `QuotationRegister_${fromDate}_${toDate}.xlsx`);

        setSnackbar({ open: true, message: 'Excel exported successfully!', severity: 'success' });
    }, [masterReportData, detailsReportData, filterPopupFromDate, filterPopupToDate]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!api || !companyId) return;
        let active = true;
        const fetchAllFirms = async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${api}/api/company-search`, { params: { field: 'FirmName', search: '', companyId, limit: 1000 } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setFirmAllOptions(opts);
                if (!(firmInputValue || '').trim()) setFirmOptions(opts);
            } catch (err) {
                if (!active) return;
                setFirmAllOptions([]);
                setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        };
        fetchAllFirms();
        return () => { active = false; };
    }, [companyId, api, filterPopupOpen]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!api || !companyId) return;
        const q = (firmInputValue || '').trim();
        if (!q) { setFirmOptions(firmAllOptions); return; }
        let active = true;
        const t = setTimeout(async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${api}/api/company-search`, { params: { field: 'FirmName', search: q, companyId } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!active) return;
                setFirmOptions(data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label));
            } catch (err) {
                if (active) setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        }, 350);
        return () => { active = false; clearTimeout(t); };
    }, [companyId, api, filterPopupOpen, firmAllOptions, firmInputValue]);

    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)`, }}>
            {/* ── Module Header ──────────────────────────────────────────── */}
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ height: 4, background: gradients.primaryRow, borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Quotations
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<FilterAltIcon />} onClick={openFilterPopup}>Filter</Button>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} component={Link} to="/main/v3/quatationMaster">
                            {isSmallDevice ? 'Add' : 'Add Quotation'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: "12px" }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 }, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {!isDesktop && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder="Search quotations..." value={filterText} onChange={(e) => setFilterText(e.target.value)} variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}

                    {!isDesktop && role !== 'User' && selectionModel.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox checked={allVisibleSelected} onChange={handleSelectAllVisible} size="small" />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {selectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete();
                                }} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {isDesktop ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                                <Paper elevation={2} sx={{ borderRadius: 3, p: 0, overflow: 'hidden', flex: 1, minHeight: 0 }}>
                                <Box sx={{ flex: 1, width: '100%', minHeight: 0, display: "flex", height: '100%', position: 'relative' }}>
                                    {loading ? <CenterLoader /> : genQuoteData?.length === 0 ? <NoDataComponent /> :
                                    <DataGrid getRowHeight={() => "auto"} rows={genQuoteData} columns={generatedColumns} getRowId={(r) => r.QoutId}disableRowSelectionOnClick checkboxSelection={role !== "User"} rowSelectionModel={selectionModel}
                                        onRowSelectionModelChange={(newSelection) => {
                                            setSelectionModel(newSelection);
                                        }}
                                        pagination pageSizeOptions={[50, 100]}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                            '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                            '& .MuiDataGrid-filler': { backgroundColor: themeColors.neutral.surface },
                                            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },
                                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                            '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },
                                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },
                                            '& .MuiDataGrid-cell': {
                                                display: 'flex',
                                                alignItems: 'center'
                                            },
                                            flex: 1
                                        }}
                                        slots={{
                                            noResultsOverlay: CustomNoDataOverlay
                                        }}
                                    />}
                                </Box>
                            </Paper>
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%', overflowX: 'hidden', px: 1, flex: 1, minHeight: 0 }}>
                            {loading ? <CenterLoader /> : genQuoteData?.length === 0 ? <NoDataComponent /> : renderGeneratedCards()}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* UNIFIED SNACKBAR - uses single snackbar state */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ whiteSpace: "pre-line" }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {pdfError && <Box sx={{ mt: 2 }}><Alert severity="error">PDF Error: {pdfError}</Alert></Box>}
            {pdfLoading && <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={18} /> <Typography>Preparing PDF...</Typography></Box>}
            {pdfUri && <Box sx={{ mt: 2 }}><PdfViewer uri={pdfUri} remoteFallbackUrl={pdfApiUrl} /></Box>}

            {pdfOverlayVisible && (
                <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.92)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
                    <Box sx={{ textAlign: "center", backgroundColor: "#111", padding: "40px 60px", borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: themeColors.neutral.surface }} />
                        <Typography variant="h6" sx={{ color: themeColors.neutral.surface, mt: 3, fontWeight: 600 }}>Generating Quotation PDF…</Typography>
                        <Typography variant="body2" sx={{ color: themeColors.neutral.muted, mt: 1 }}>Please wait</Typography>
                    </Box>
                </Box>
            )}

            {/* ── Delete Confirmation Dialog ──────────────────────────── */}
            <Dialog open={openDeleteDialog} onClose={() => { if (!deleting) setOpenDeleteDialog(false); }} maxWidth="xs" fullWidth>
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: gradients.danger }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{selectionModel.length}</Typography>
                        {' '}quotation(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>

            {/* Filter Popup */}
            <Dialog open={filterPopupOpen} onClose={cancelFilterPopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}>
                {/* Header */}
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, pt: 2, px: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FilterListIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            Filter Quotations
                        </Typography>
                    </Box>
                    <IconButton onClick={cancelFilterPopup} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ px: 3, py: 2, bgcolor: themeColors.neutral.surfaceAlt }}>
                    {/* Filter Mode */}
                    <Paper elevation={0} sx={{ p: 2, mb: 1, mt: 1, bgcolor: 'white', border: `1px solid ${themeColors.neutral.line}`, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                                Filter Mode
                            </Typography>
                        </Box>
                        <RadioGroup row value={filterPopupMode} onChange={(e) => setFilterPopupMode(e.target.value)} sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                                value="datewise"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>📅 Datewise</Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1, m: 0,
                                    bgcolor: filterPopupMode === 'datewise' ? themeColors.primary.tint : themeColors.neutral.surfaceMuted,
                                    borderRadius: 1.5, px: 2, py: 0.5,
                                    border: filterPopupMode === 'datewise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'datewise' ? themeColors.primary.tint : themeColors.neutral.border }
                                }}
                            />
                            <FormControlLabel
                                value="partywise"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>👥 Partywise</Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1, m: 0,
                                    bgcolor: filterPopupMode === 'partywise' ? themeColors.primary.tint : themeColors.neutral.surfaceMuted,
                                    borderRadius: 1.5, px: 2, py: 0.5,
                                    border: filterPopupMode === 'partywise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { bgcolor: filterPopupMode === 'partywise' ? themeColors.primary.tint : themeColors.neutral.border }
                                }}
                            />
                        </RadioGroup>
                    </Paper>

                    {/* Filter Fields */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Date Range */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: `1px solid ${themeColors.neutral.line}`, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📆 Date Range
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                        From Date
                                    </Typography>
                                    <TextField
                                        fullWidth size="small" type="date"
                                        value={filterPopupFromDate}
                                        onChange={(e) => setFilterPopupFromDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { bgcolor: themeColors.neutral.surfaceAlt, borderRadius: 1.5, '&:hover': { bgcolor: themeColors.neutral.surfaceMuted }, '&.Mui-focused': { bgcolor: 'white' } },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.neutral.line }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                                        To Date
                                    </Typography>
                                    <TextField
                                        fullWidth size="small" type="date"
                                        value={filterPopupToDate}
                                        onChange={(e) => setFilterPopupToDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { bgcolor: themeColors.neutral.surfaceAlt, borderRadius: 1.5, '&:hover': { bgcolor: themeColors.neutral.surfaceMuted }, '&.Mui-focused': { bgcolor: 'white' } },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.neutral.line }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>

                        {/* FirmName */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', border: `1px solid ${themeColors.neutral.line}`, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#4ecdc4', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    🏢 FirmName
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple disableCloseOnSelect
                                options={firmOptions} loading={firmLoading}
                                value={filterPopupFirms}
                                onChange={(_, newValue) => setFilterPopupFirms(newValue)}
                                inputValue={firmInputValue}
                                onInputChange={(_, v) => setFirmInputValue(v)}
                                getOptionLabel={(o) => o?.label || ''}
                                isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)}
                                renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={selected} size="small" sx={{ mr: 1, color: '#4ecdc4', '&.Mui-checked': { color: '#4ecdc4' } }} />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params} fullWidth size="small"
                                        placeholder={filterPopupFirms?.length ? `${filterPopupFirms.length} firm(s) selected` : 'Select firms (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': { bgcolor: themeColors.neutral.surfaceAlt, borderRadius: 1.5, '&:hover': { bgcolor: themeColors.neutral.surfaceMuted }, '&.Mui-focused': { bgcolor: 'white' } },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.neutral.line }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupFirms?.length > 0 && (
                                                        <Chip label={`${filterPopupFirms.length}`} size="small" sx={{ ml: 0.5, height: 22, bgcolor: '#4ecdc4', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ px: 3, py: 2.5, bgcolor: themeColors.neutral.surfaceAlt, borderTop: `1px solid ${themeColors.neutral.line}`, gap: 2 }}>
                    <Button
                        onClick={cancelFilterPopup}
                        variant="outlined"
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2,
                            borderColor: themeColors.neutral.line, color: 'text.secondary',
                            '&:hover': { borderColor: themeColors.neutral.muted, bgcolor: themeColors.neutral.surfaceMuted }
                        }}
                    >
                        ✕ Cancel
                    </Button>

                    <Button
                        onClick={handleExportExcel}
                        variant="outlined"
                        disabled={!exportExcelEnabled}
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2,
                            borderColor: !exportExcelEnabled ? themeColors.neutral.line : themeColors.success.dark,
                            color: !exportExcelEnabled ? 'text.disabled' : themeColors.success.dark,
                            '&:hover': {
                                borderColor: themeColors.success.dark,
                                bgcolor: '#f1f8e9'
                            },
                            '&.Mui-disabled': {
                                borderColor: themeColors.neutral.line,
                                color: 'text.disabled'
                            }
                        }}
                    >
                        📊 Export Excel
                    </Button>

                    <Button
                        onClick={handleFilterOk}
                        variant="contained"
                        disabled={reportLoading}
                        startIcon={reportLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                            },
                            '&.Mui-disabled': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                opacity: 0.7,
                                color: 'white'
                            }
                        }}
                    >
                        ✓ {reportLoading ? 'Loading...' : 'Apply Filters'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}